import {
  capitalize,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
} from "@mui/material";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { HitchType } from "protos/atk/enums";
import { hitchTypeToLocaleString } from "common/utils/enumTranslations";
import { negateVal } from "common/utils/negateVal";
import { StepProps } from "./StepProps";
import { TractorViewModel } from "../TractorViewModel";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC, useState } from "react";

export const HitchStep: FC<StepProps> = ({ disabled }) => {
  const formikProps = useFormikContext<TractorViewModel>();
  const { values } = formikProps;
  const { t } = useTranslation();

  const [checkedOverrideBox, setCheckedOverrideBox] = useState<boolean>(
    Boolean(formikProps.values.hitches)
  );

  const hasCustomInfo =
    Boolean(formikProps.values.hitches) || checkedOverrideBox;

  const isDisabled = disabled || !hasCustomInfo;

  return (
    <div className="flex flex-col gap-2">
      <Paper className="flex flex-col gap-2 p-4">
        <div className="flex gap-1">
          <InfoOutlinedIcon />
          <Typography>
            {capitalize(t("views.autotractor.equipment.customInfoBlurb"))}
          </Typography>
        </div>
        <FormControlLabel
          disabled={disabled}
          label={t("views.autotractor.equipment.tractors.hasCustomHitchInfo")}
          checked={hasCustomInfo}
          control={<Checkbox />}
          onChange={() => {
            if (checkedOverrideBox) {
              formikProps.setFieldValue("hitches", undefined);
            } else {
              formikProps.setFieldValue("hitches", values.archetype?.hitches);
            }
            setCheckedOverrideBox(!checkedOverrideBox);
          }}
        />
      </Paper>
      {hasCustomInfo && (
        <Paper className="flex flex-col p-4">
          <Typography className="font-bold">
            {capitalize(t("models.equipment.hitch_other"))}
          </Typography>
          <div className="px-4 py-2">
            <Typography>{capitalize(t("utils.descriptors.front"))}</Typography>
            <div className="flex flex-col gap-2 px-4">
              <HitchInput
                name="hitches"
                disabled={isDisabled}
                type={HitchType.HITCH_TYPE_THREE_POINT_FRONT}
              />
            </div>
            <Typography>{capitalize(t("utils.descriptors.rear"))}</Typography>
            <div className="flex flex-col gap-2 px-4">
              <HitchInput
                name="hitches"
                disabled={isDisabled}
                type={HitchType.HITCH_TYPE_THREE_POINT_REAR}
              />
              <HitchInput
                name="hitches"
                disabled={isDisabled}
                type={HitchType.HITCH_TYPE_DRAW_BAR_REAR}
              />
            </div>
          </div>
        </Paper>
      )}{" "}
      {!hasCustomInfo && (
        <Paper className="flex flex-col p-4">
          <Typography className="font-bold">
            {capitalize(t("models.equipment.hitch_other"))}
          </Typography>
          <div className="px-4 py-2">
            <Typography>{capitalize(t("utils.descriptors.front"))}</Typography>
            <div className="flex flex-col gap-2 px-4">
              <HitchInput
                name="archetype.hitches"
                disabled={isDisabled}
                type={HitchType.HITCH_TYPE_THREE_POINT_FRONT}
              />
            </div>
            <Typography>{capitalize(t("utils.descriptors.rear"))}</Typography>
            <div className="flex flex-col gap-2 px-4">
              <HitchInput
                name="archetype.hitches"
                disabled={isDisabled}
                type={HitchType.HITCH_TYPE_THREE_POINT_REAR}
              />
              <HitchInput
                name="archetype.hitches"
                disabled={isDisabled}
                type={HitchType.HITCH_TYPE_DRAW_BAR_REAR}
              />
            </div>
          </div>
        </Paper>
      )}
    </div>
  );
};

interface HitchInputProps {
  type: HitchType;
  name: "hitches" | "archetype.hitches";
  disabled?: boolean;
}
const HitchInput: FC<HitchInputProps> = ({ type, disabled, name }) => {
  const { values, setFieldValue } = useFormikContext<TractorViewModel>();
  const hitchesValue =
    name === "archetype.hitches" ? values.archetype?.hitches : values.hitches;
  const indexOfHitch = (hitchType: HitchType): number => {
    return hitchesValue?.hitches.findIndex((v) => v.type === hitchType) ?? -1;
  };
  const hitchExists = (hitchType: HitchType): boolean => {
    return indexOfHitch(hitchType) >= 0;
  };

  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const typeName = `${name}.hitches[${indexOfHitch(type)}]`;

  return (
    <div className="flex justify-between min-h-20">
      <FormControlLabel
        disabled={disabled}
        checked={hitchExists(type)}
        onChange={() => {
          if (hitchExists(type)) {
            setFieldValue(name, {
              hitches: hitchesValue?.hitches.filter((h) => h.type !== type),
            });
          } else {
            setFieldValue(name, {
              hitches: [
                ...(hitchesValue?.hitches ?? []),
                { type, offsetYM: 0 },
              ],
            });
          }
        }}
        control={<Checkbox />}
        label={t(hitchTypeToLocaleString(type))}
      />
      {hitchExists(type) && (
        <FormikDimensionedNumberInput
          label={
            type === HitchType.HITCH_TYPE_THREE_POINT_FRONT
              ? capitalize(t("models.equipment.distanceAheadFrontAxle"))
              : capitalize(t("models.equipment.distanceBehindFrontAxle"))
          }
          name={`${typeName}.offsetYM`}
          tProps={{ minimum: 0 }}
          toStored={
            type === HitchType.HITCH_TYPE_THREE_POINT_FRONT
              ? negateVal
              : undefined
          }
          fromStored={
            type === HitchType.HITCH_TYPE_THREE_POINT_FRONT
              ? negateVal
              : undefined
          }
          dimensionedNumberInputProps={{
            storedUnit: "m",
            localSystem: measurementSystem,
            disabled,
            displayUnits: { imperial: "in", metric: "cm" },
          }}
        />
      )}
    </div>
  );
};
